import {castArray} from 'lodash';
import {DateFilter, DomainValueReference, PropertyFilter, Value, ValueDefinition, ValueFilter} from '@/model/types';
import {DOMAIN_VALUE_PREFIX} from '@/model/types/constants';
import {ValueResolver} from './types';
import {normalizeDateInput, validateDate} from './utils/date';

export type FilterFn = (value: Value, definition: ValueDefinition) => boolean;

type FilterFnFactory<T extends FilterOperator> = (
  filter: Omit<Extract<DateFilter | ValueFilter | PropertyFilter, {operator: T}>, 'operator'>,
  valueResolver: ValueResolver,
) => FilterFn;

type FilterOperator = (DateFilter | ValueFilter | PropertyFilter)['operator'];

type FilterFnMap = {
  [T in FilterOperator]: FilterFnFactory<T>;
};

export const PropertyHasFilterFn: FilterFnFactory<'has'> =
  (filter, resolver): FilterFn =>
  (value, definition) => {
    const resolvedField =
      typeof filter.field === 'string' && filter.field.startsWith(DOMAIN_VALUE_PREFIX)
        ? resolver({$ref: filter.field})
        : resolver(filter.field);

    const propertyValues = castArray(
      resolver<string[]>({
        $ref: `${definition.id}#${value.id}`,
        property: filter.property,
      }),
    );

    const resolvedDefintionValues = propertyValues.map(v => resolver({$ref: v}));

    return resolvedDefintionValues.includes(resolvedField);
  };

export const PropertyNotHasFilterFn: FilterFnFactory<'not has'> = (filter, resolver): FilterFn => {
  const includeFilterFn = PropertyHasFilterFn(filter, resolver);
  return (value, definition) => !includeFilterFn(value, definition);
};

export const PropertyIsFilterFn: FilterFnFactory<'is'> = (filter, resolver): FilterFn => {
  if (typeof filter.value === 'string') {
    return (value, definition) =>
      filter.value === resolver({$ref: `${definition.id}#${value.id}`, property: filter.property});
  }

  return (value, definition) => {
    const resolvedValue = resolver(filter.value);
    return resolvedValue === resolver({$ref: `${definition.id}#${value.id}`, property: filter.property});
  };
};

export const PropertyIsNotFilterFn: FilterFnFactory<'is not'> = (filter, resolver): FilterFn => {
  const isFilterFn = PropertyIsFilterFn(filter, resolver);
  return (value, definition) => !isFilterFn(value, definition);
};

export const InFilterFn: FilterFnFactory<'in'> = (filter, resolver): FilterFn => {
  if (Array.isArray(filter.value)) {
    return (value, definition) => {
      const resolvedValues = (filter.value as string[]).map(value => resolver({$ref: value}));
      const resolvedDefintionValue = resolver({$ref: `${definition.id}#${value.id}`});
      return resolvedValues.includes(resolvedDefintionValue);
    };
  }

  if (filter.value && '$query' in filter.value) {
    const values = resolver<string[]>(filter.value) ?? [];
    return (value, definition) => values.includes(`${definition.id}#${value.id}`);
  }

  if (filter.value && filter.value.property !== undefined) {
    const values = resolver(filter.value as DomainValueReference) ?? [];
    return (value, definition) => values.includes(`${definition.id}#${value.id}`);
  }

  throw new Error(`ValueFilter must be an array or a reference to a domain value with a property`);
};

export const NotInFilterFn: FilterFnFactory<'not in'> = (filter, resolver): FilterFn => {
  const includeFilterFn = InFilterFn(filter, resolver);
  return (value, definition) => !includeFilterFn(value, definition);
};

export const DateFilterFn: FilterFnFactory<'date'> = (filter): FilterFn => {
  return (value, definition) => {
    if (!('properties' in value)) {
      throw new Error(`Value '${definition.id}#${value.id}' is not a custom value`);
    }

    return validateDate(filter.value)(normalizeDateInput(value.properties[filter.property] as string));
  };
};

const filterFnMap: FilterFnMap = {
  'is': PropertyIsFilterFn,
  'is not': PropertyIsNotFilterFn,
  'in': InFilterFn,
  'not in': NotInFilterFn,
  'date': DateFilterFn,
  'has': PropertyHasFilterFn,
  'not has': PropertyNotHasFilterFn,
};

export const getFilterFn = <K extends FilterOperator>(
  filter: Extract<DateFilter | ValueFilter | PropertyFilter, {operator: K}>,
): ((valueResolver: ValueResolver) => FilterFn) => {
  if (!filterFnMap[filter.operator]) {
    throw new Error(`Unknown condition operator: ${filter.operator}`);
  }

  return valueResolver => filterFnMap[filter.operator](filter, valueResolver);
};
